import { cn } from "@/lib/utils";
import {
  Chat as ChatType,
  ChatWithMessagesType,
  Message as MessageType,
} from "@/types/chat";
import { Link, router, useForm, usePage } from "@inertiajs/react";
import { formatDistanceToNowStrict, intlFormat } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowLeftIcon,
  CheckCheckIcon,
  CheckIcon,
  ChevronDownIcon,
  LoaderCircleIcon,
  MessageSquareIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SendIcon,
  TagIcon,
  XIcon,
} from "lucide-react";
import {
  createContext,
  use,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useQueryState } from "nuqs";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { MessageContent, isSticker } from "@/components/chat/message-content";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "../ui/item";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";

type ChatContextType = {
  selectedChat: ChatWithMessagesType | null;
  chats: ChatType[];
  canSendMessage: boolean;
  canMarkSale: boolean;
  saleMarkedInLast24h: boolean;
  archivedMode: boolean;
  isArchived: boolean;
  sendMessage: (contactId: number, text: string) => void;
};

export const ChatContext = createContext<ChatContextType>({
  selectedChat: null,
  chats: [],
  canSendMessage: true,
  canMarkSale: false,
  saleMarkedInLast24h: false,
  archivedMode: false,
  isArchived: false,
  sendMessage: () => {},
});

const MessageContext = createContext<MessageType | undefined>(undefined);

// Format digits to Brazilian phone: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
function formatBrazilianPhone(digits: string): string {
  const d = digits.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function stripPhoneDigits(formatted: string): string {
  return formatted.replace(/\D/g, "");
}

function MessageStatus() {
  const messageContext = use(MessageContext);
  if (!messageContext!.self) return null;

  const Icon = messageContext!.status === "sent" ? CheckIcon : CheckCheckIcon;
  const isRead = messageContext!.status === "read";

  return (
    <Icon
      className={cn("size-4 shrink-0", isRead ? "text-blue-500" : "opacity-60")}
    />
  );
}

function MessageMeta() {
  const messageContext = use(MessageContext);
  const isSelf = messageContext!.self;

  const timestamp = intlFormat(
    messageContext!.timestamp,
    {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    },
    { locale: "pt-BR" },
  );

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[0.65rem] leading-none opacity-60 select-none",
        !isSelf && "py-1",
      )}
    >
      {timestamp}
      <MessageStatus />
    </span>
  );
}

function Message({ message }: { message: MessageType }) {
  const isSelf = message.self;
  const sticker = isSticker(message);

  return (
    <MessageContext.Provider value={message}>
      <div className={cn("flex", isSelf ? "justify-end" : "justify-start")}>
        <div
          className={cn(
            "flex flex-col max-w-[75%] rounded-lg text-sm",
            sticker
              ? "items-center"
              : cn(
                  "px-3 py-1.5 shadow-sm",
                  isSelf
                    ? "bg-bubble-outgoing text-bubble-outgoing-foreground rounded-tr-none items-end"
                    : "bg-bubble-incoming text-bubble-incoming-foreground rounded-tl-none items-start",
                ),
          )}
        >
          <MessageContent message={message} />
          <MessageMeta />
        </div>
      </div>
    </MessageContext.Provider>
  );
}

function MessageList({ className }: { className?: string }) {
  const chatContext = use(ChatContext);
  const chat = chatContext.selectedChat!;
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Auto-scroll on new messages if already near bottom
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    if (distanceFromBottom < 150) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat.messages.length]);

  // Scroll to bottom on initial load
  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [chat.id]);

  // Track scroll position
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const handleScroll = () => {
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      setShowScrollButton(distanceFromBottom > 150);
    };

    viewport.addEventListener("scroll", handleScroll);
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [chat.id]);

  return (
    <div className={cn("relative min-w-0", className)}>
      <ScrollArea className="h-full [&>[data-slot=scroll-area-viewport]>div]:!block" ref={viewportRef}>
        <div className="flex flex-col gap-1 p-4">
          {chat.messages.map((message) => (
            <Message key={message.id} message={message} />
          ))}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
      {showScrollButton && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute bottom-2 right-4 rounded-full shadow-md z-10"
          onClick={scrollToBottom}
        >
          <ChevronDownIcon />
        </Button>
      )}
    </div>
  );
}

function NewChatButton() {
  const [open, setOpen] = useState(false);
  const { data, setData, post, processing, errors, reset } = useForm({
    name: "",
    phone: "",
  });
  const [formattedPhone, setFormattedPhone] = useState("");

  function handlePhoneChange(value: string) {
    const formatted = formatBrazilianPhone(value);
    setFormattedPhone(formatted);
    setData("phone", stripPhoneDigits(formatted));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    post("/app/contacts", {
      onSuccess: () => {
        setOpen(false);
        reset();
        setFormattedPhone("");
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { reset(); setFormattedPhone(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-full">
          <PlusIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo contato</DialogTitle>
          <DialogDescription>
            Registre um contato para iniciar uma conversa.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-name">Nome</Label>
            <Input
              id="contact-name"
              type="text"
              placeholder="João Silva"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              disabled={processing}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-phone">Telefone</Label>
            <Input
              id="contact-phone"
              type="tel"
              placeholder="(11) 99988-7766"
              value={formattedPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              disabled={processing}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              {processing && <LoaderCircleIcon className="size-4 animate-spin" />}
              Criar contato
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PersonAvatar({ avatar }: { avatar: string | null }) {
  if (!avatar) {
    return (
      <div className="flex items-center justify-center size-full bg-muted text-muted-foreground text-xs font-medium rounded-full">
        ?
      </div>
    );
  }
  return <img src={avatar} alt="Avatar" className="" />;
}

function LastMessageTime({
  message,
  className,
}: {
  message: MessageType;
  className?: string;
}) {
  const relative = formatDistanceToNowStrict(message.timestamp, {
    locale: ptBR,
  });
  return (
    <div className={cn("text-muted-foreground text-xs", className)}>
      {relative}
    </div>
  );
}

function UnreadCount({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  return count > 0 ? (
    <Badge className={cn("text-xs", className)}>{count}</Badge>
  ) : null;
}

function ChatListItem({
  chat,
  selected,
}: {
  chat: ChatType;
  selected: boolean;
}) {
  return (
    <Item variant={selected ? "outline" : "default"} size="xs" asChild>
      <Link href={chat.chatUrl}>
        <ItemMedia variant="image">
          <PersonAvatar avatar={chat.avatar} />
        </ItemMedia>
        <ItemContent>
          <div className="grid grid-cols-[1fr_auto] gap-1">
            <ItemTitle className="line-clamp-1 wrap-anywhere">
              {chat.name}
            </ItemTitle>
            {chat.lastMessage && (
              <LastMessageTime message={chat.lastMessage} className="ml-auto" />
            )}
            <ItemDescription className="line-clamp-1">
              {chat.lastMessage?.text ?? "-"}
            </ItemDescription>
            <UnreadCount count={chat.unreadCount} className="ml-auto" />
          </div>
        </ItemContent>
      </Link>
    </Item>
  );
}

function ChatList() {
  const chatContext = use(ChatContext);

  if (chatContext.chats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
        {chatContext.archivedMode ? (
          <>
            <ArchiveIcon className="size-10 opacity-50" />
            <p className="text-sm">Nenhuma conversa arquivada</p>
          </>
        ) : (
          <>
            <MessageSquareIcon className="size-10 opacity-50" />
            <p className="text-sm">Nenhuma conversa encontrada</p>
          </>
        )}
      </div>
    );
  }

  return (
    <ItemGroup className="gap-0.5">
      {chatContext.chats.map((chat) => (
        <ChatListItem
          key={chat.id}
          chat={chat}
          selected={chatContext.selectedChat?.id === chat.id}
        />
      ))}
    </ItemGroup>
  );
}

function SearchChatInput() {
  const [queryParam, setQueryParam] = useQueryState("q", {
    defaultValue: "",
    shallow: true,
  });
  const [inputValue, setInputValue] = useState(queryParam);
  const inputRef = useRef<HTMLInputElement>(null);

  const reloadChats = useDebouncedCallback((value: string) => {
    setQueryParam(value || null);
    router.reload({
      data: { q: value || undefined },
      only: ["chats"],
    });
  }, 300);

  function handleChange(value: string) {
    setInputValue(value);
    reloadChats(value.trim());
  }

  function handleClear() {
    setInputValue("");
    setQueryParam(null);
    router.reload({
      data: {},
      only: ["chats"],
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      handleClear();
      inputRef.current?.blur();
    }
  }

  return (
    <div className="relative">
      <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        type="text"
        placeholder="Buscar conversas..."
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="pl-8 pr-8"
      />
      {inputValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

export function ChatSidebar({ className }: { className?: string }) {
  const { archivedMode } = use(ChatContext);

  return (
    <Card className={cn("w-64 shrink-0 min-h-0 flex flex-col lg:w-80", className)}>
      <CardHeader>
        <CardTitle>Conversas</CardTitle>
        <CardDescription>
          {archivedMode
            ? "Conversas arquivadas são somente leitura."
            : "Inicie uma nova conversa ou busque nas conversas existentes."}
        </CardDescription>
        {!archivedMode && (
          <CardAction>
            <NewChatButton />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex-1 min-h-0 flex flex-col gap-4">
        <div className="flex gap-1">
          <Button
            variant={archivedMode ? "ghost" : "default"}
            size="sm"
            className="flex-1"
            onClick={() => router.get("/app/chats")}
          >
            Ativas
          </Button>
          <Button
            variant={archivedMode ? "default" : "ghost"}
            size="sm"
            className="flex-1"
            onClick={() => router.get("/app/chats", { archived: true })}
          >
            <ArchiveIcon className="size-4" />
            Arquivadas
          </Button>
        </div>
        <SearchChatInput />
        <ScrollArea className="flex-1 min-h-0">
          <ChatList />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function MessageInput() {
  const chatContext = use(ChatContext);
  const chat = chatContext.selectedChat!;
  const [text, setText] = useState("");
  const { organization } = usePage().props;
  const noOrgMode = !organization;

  if (chatContext.isArchived || !chatContext.canSendMessage) return null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;

    chatContext.sendMessage(chat.id, text.trim());
    setText("");
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <Input
        type="text"
        placeholder={noOrgMode ? "Selecione uma organização para enviar mensagens" : "Digite uma mensagem..."}
        className="flex-1"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={noOrgMode}
      />
      <Button
        type="submit"
        size="icon"
        disabled={!text.trim() || noOrgMode}
      >
        <SendIcon />
      </Button>
    </form>
  );
}

function MarkSaleButton() {
  const { selectedChat, saleMarkedInLast24h } = use(ChatContext);
  const [processing, setProcessing] = useState(false);

  function handleConfirm() {
    setProcessing(true);
    router.post(`/app/chats/${selectedChat!.id}/mark_sale`, {}, {
      preserveScroll: true,
      onFinish: () => setProcessing(false),
    });
  }

  if (saleMarkedInLast24h) {
    return (
      <Button variant="outline" size="sm" disabled>
        <CheckIcon className="size-4" />
        Venda Registrada
      </Button>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={processing}>
          {processing ? (
            <LoaderCircleIcon className="size-4 animate-spin" />
          ) : (
            <TagIcon className="size-4" />
          )}
          Marcar Venda
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Registrar venda</AlertDialogTitle>
          <AlertDialogDescription>
            Registrar venda para este contato?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Confirmar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RenameContactButton() {
  const { selectedChat } = use(ChatContext);
  const [open, setOpen] = useState(false);
  const { data, setData, patch, processing, errors, reset } = useForm({
    name: selectedChat?.name ?? "",
  });

  useEffect(() => {
    if (selectedChat) setData("name", selectedChat.name);
  }, [selectedChat?.id]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    patch(`/app/contacts/${selectedChat!.id}`, {
      onSuccess: () => setOpen(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <PencilIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renomear contato</DialogTitle>
          <DialogDescription>
            Altere o nome de exibição deste contato.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-name">Nome</Label>
            <Input
              id="rename-name"
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              disabled={processing}
              autoFocus
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              {processing && <LoaderCircleIcon className="size-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveContactButton() {
  const { selectedChat } = use(ChatContext);
  const [processing, setProcessing] = useState(false);

  function handleConfirm() {
    setProcessing(true);
    router.delete(`/app/contacts/${selectedChat!.id}`, {
      onFinish: () => setProcessing(false),
    });
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" disabled={processing}>
          {processing ? (
            <LoaderCircleIcon className="size-4 animate-spin" />
          ) : (
            <ArchiveIcon className="size-4" />
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Arquivar contato</AlertDialogTitle>
          <AlertDialogDescription>
            O contato será removido da lista de conversas. Seus dados serão
            preservados para consultas futuras.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Arquivar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RestoreContactButton() {
  const { selectedChat } = use(ChatContext);
  const [processing, setProcessing] = useState(false);

  function handleConfirm() {
    setProcessing(true);
    router.patch(`/app/contacts/${selectedChat!.id}/restore`, {}, {
      onFinish: () => setProcessing(false),
    });
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={processing}>
          {processing ? (
            <LoaderCircleIcon className="size-4 animate-spin" />
          ) : (
            <ArchiveRestoreIcon className="size-4" />
          )}
          Desarquivar
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Desarquivar contato</AlertDialogTitle>
          <AlertDialogDescription>
            O contato será restaurado para a lista de conversas ativas.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Desarquivar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ChatHeader() {
  const chatContext = use(ChatContext);
  const chat = chatContext.selectedChat!;

  const backHref = chatContext.archivedMode ? "/app/chats?archived=true" : "/app/chats";

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          asChild
        >
          <Link href={backHref}>
            <ArrowLeftIcon className="size-4" />
          </Link>
        </Button>
        <Item size="sm">
          <ItemMedia variant="image">
            <PersonAvatar avatar={chat.avatar} />
          </ItemMedia>
          <ItemContent>
            <ItemTitle className="line-clamp-1">{chat.name}</ItemTitle>
            <ItemDescription>{chat.remoteJid}</ItemDescription>
          </ItemContent>
        </Item>
      </div>
      <div className="flex items-center gap-1">
        {chatContext.isArchived ? (
          <RestoreContactButton />
        ) : (
          <>
            <RenameContactButton />
            <ArchiveContactButton />
            {chatContext.canMarkSale && <MarkSaleButton />}
          </>
        )}
      </div>
    </div>
  );
}

export function Chat() {
  const chatContext = use(ChatContext);

  if (!chatContext.selectedChat) {
    return (
      <div className="flex items-center justify-center flex-1 text-muted-foreground">
        Selecione uma conversa para começar
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0 min-w-0">
      <ChatHeader />
      <MessageList className="flex-1 min-h-0" />
      <MessageInput />
    </div>
  );
}
